import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import { Building2, User, Phone, Mail, MapPin } from "lucide-react";
import { toast } from "sonner";

const AgencyForm = () => {
  const { t, language } = useLanguage();
  const [formData, setFormData] = useState({
    agencyName: "",
    contactPerson: "",
    phone: "",
    email: "",
    address: "",
    message: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await fetch("/agency.php", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData),
      });

      const result = await response.json();

      if (result.success) {
        toast.success(
          language === "tr"
            ? "Başvurunuz başarıyla alındı!"
            : "Your application has been received successfully!"
        );
        setFormData({
          agencyName: "",
          contactPerson: "",
          phone: "",
          email: "",
          address: "",
          message: "",
        });
      } else {
        toast.error(result.message || "Bir hata oluştu!");
      }
    } catch (err) {
      toast.error("Sunucuya ulaşılamıyor!");
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50">
      {/* Header */}
      <section className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-20">
        <div className="container mx-auto px-4">
          <h1 className="text-5xl font-bold mb-4 text-center">
            {t("agencyFormTitle")}
          </h1>
          <p className="text-xl text-gray-300 text-center max-w-2xl mx-auto">
            {t("agencyFormDesc")}
          </p>
        </div>
      </section>

      <div className="container mx-auto px-4 py-16">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Agency Name */}
              <div>
                <label
                  htmlFor="agencyName"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("agencyName")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Building2 className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="agencyName"
                    name="agencyName"
                    value={formData.agencyName}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    placeholder={
                      language === "tr"
                        ? "Acenta adınızı girin"
                        : "Enter your agency name"
                    }
                  />
                </div>
              </div>

              {/* Contact Person */}
              <div>
                <label
                  htmlFor="contactPerson"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("contactPerson")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    id="contactPerson"
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    placeholder={
                      language === "tr"
                        ? "Yetkili kişi adı soyadı"
                        : "Contact person name"
                    }
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label
                  htmlFor="phone"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("phone")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Phone className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    placeholder="+90 XXX XXX XX XX"
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("email")}
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300"
                    placeholder="info@example.com"
                  />
                </div>
              </div>

              {/* Address */}
              <div>
                <label
                  htmlFor="address"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {t("address")}
                </label>
                <div className="relative">
                  <div className="absolute top-3 left-0 pl-4 flex items-start pointer-events-none">
                    <MapPin className="h-5 w-5 text-gray-400" />
                  </div>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 resize-none"
                    placeholder={
                      language === "tr"
                        ? "Tam adresinizi girin"
                        : "Enter your full address"
                    }
                  />
                </div>
              </div>

              {/* Message */}
              <div>
                <label
                  htmlFor="message"
                  className="block text-sm font-medium text-gray-700 mb-2"
                >
                  {language === "tr"
                    ? "Ek Bilgiler (Opsiyonel)"
                    : "Additional Information (Optional)"}
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent transition-all duration-300 resize-none"
                  placeholder={
                    language === "tr"
                      ? "İş birliği ile ilgili detaylar..."
                      : "Details about partnership..."
                  }
                />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="w-full bg-gradient-to-r from-amber-500 to-yellow-600 text-white px-8 py-4 rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300"
              >
                {t("submit")}
              </button>
            </form>

            {/* Info Box */}
            <div className="mt-8 bg-gradient-to-br from-amber-50 to-yellow-50 rounded-xl p-6">
              <h3 className="font-semibold text-gray-900 mb-2">
                {language === "tr" ? "Neden SunKiss?" : "Why SunKiss?"}
              </h3>
              <ul className="space-y-2 text-gray-700 text-sm">
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>
                    {language === "tr"
                      ? "Rekabetçi komisyon oranları"
                      : "Competitive commission rates"}
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>
                    {language === "tr"
                      ? "Geniş otel ve tur seçenekleri"
                      : "Wide range of hotel and tour options"}
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>
                    {language === "tr"
                      ? "7/24 müşteri desteği"
                      : "24/7 customer support"}
                  </span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-amber-600 font-bold">•</span>
                  <span>
                    {language === "tr"
                      ? "Hızlı rezervasyon sistemi"
                      : "Fast reservation system"}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgencyForm;
